import React, { useState, useEffect } from 'react';
import { useSyncedTab } from '../../hooks/useSyncedTab';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGuard from '../Common/PermissionGuard';
import LoadingSpinner from '../Common/LoadingSpinner';
import CalendarView from './CalendarView';
import { getLeadStats } from '../../services/crmApi';
import toast from 'react-hot-toast';
import {
  aggregateStatusCountsByGroup,
  getLeadStatusLabel,
  GROUP_DISPLAY_LABELS
} from '../../utils/leadStatus';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useSyncedTab('overview');
  const { user, hasPermission } = useAuth();
  const [leadStats, setLeadStats] = useState(null);
  const [leadStatsLoading, setLeadStatsLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchDashboardData();
    }
  }, [activeTab]);

  const fetchDashboardData = async () => {
    // Fetch lead stats for analysis section
    // /crm/leads/stats is org-wide and requires CRM:LEADS:READ; My Leads-only users would just get a 403 toast.
    if (hasPermission('CRM:LEADS:READ')) {
      setLeadStatsLoading(true);
      try {
        const res = await getLeadStats();
        setLeadStats(res.data?.data || null);
      } catch (err) {
        console.error('Error fetching lead stats:', err);
        toast.error('Failed to load lead statistics');
      } finally {
        setLeadStatsLoading(false);
      }
    }
  };

  if (!user) {
    return <LoadingSpinner size="md" message="Loading user data..." />;
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'overview'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="w-5 h-5 inline-block mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
            Overview
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'calendar'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="w-5 h-5 inline-block mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            Calendar
          </button>
        </div>
      </div>

      {/* Conditional Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Welcome Section */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-8 sm:p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Welcome back, {String(user?.name || 'User')}!
              </h1>
              <p className="text-indigo-100">
                Here's what's happening with your business today.
              </p>
            </div>
            <div className="hidden sm:block">
              <div className="w-16 h-16 bg-indigo-500 bg-opacity-30 rounded-full flex items-center justify-center backdrop-blur-sm">
                <svg className="w-8 h-8 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Analysis Section */}
      {(hasPermission('CRM:LEADS:READ') || hasPermission('CRM:MY_LEADS:READ')) && (() => {
        // Show each status individually (not grouped), only statuses with data
        const STATUS_COLORS = {
          unattended: '#3b82f6',
          allocated: '#8b5cf6',
          qualified: '#22c55e',
          'not-interested': '#ef4444',
          'wrong-number': '#b91c1c',
          'invalid-lead': '#b91c1c',
          'wrong-number-invalid-lead': '#b91c1c',
          'positive-send-details': '#14b8a6',
          'no-answer-call-later': '#f59e0b',
          'entry-not-met': '#ec4899',
          'transfer-to-another-programme': '#06b6d4',
          'next-intake': '#6366f1',
          'application-link-shared': '#0ea5e9',
          'application-submitted': '#2563eb',
          'payment-verification-pending': '#f97316',
          'payment-rejected': '#dc2626',
          'document-pending': '#f59e0b',
          'documents-approved': '#84cc16',
          'application-rejected': '#e11d48',
          'application-approved': '#16a34a',
          'in-progress': '#f97316',
          registered: '#16a34a'
        };

        const formatGroupedLabel = (status) => {
          if (status === 'in-progress') return GROUP_DISPLAY_LABELS.inProgress;
          if (status === 'registered') return GROUP_DISPLAY_LABELS.registered;
          return getLeadStatusLabel(status);
        };

        const groupedByStatus = aggregateStatusCountsByGroup(leadStats?.byStatus || {});

        const pipelineData = Object.entries(groupedByStatus)
          .filter(([status, count]) => {
            // Filter out null/undefined/empty statuses and zero counts
            const validStatus = status && status !== 'null' && status !== 'undefined' && String(status).trim() !== '';
            const validCount = count > 0;
            return validStatus && validCount;
          })
          .map(([status, count]) => ({
            name: formatGroupedLabel(status),
            count: count,
            color: STATUS_COLORS[status] || '#6b7280'
          }))
          .sort((a, b) => b.count - a.count); // Sort by count descending

        const total = leadStats?.total || 0;

        const CustomBarTooltip = ({ active, payload }) => {
          if (active && payload?.length) {
            const pct = total > 0 ? ((payload[0].value / total) * 100).toFixed(1) : 0;
            return (
              <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                <p className="font-medium text-gray-900">{payload[0].payload.name}</p>
                <p className="text-gray-600">{payload[0].value} leads <span className="text-gray-400">({pct}%)</span></p>
              </div>
            );
          }
          return null;
        };

        const Spinner = () => (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        );

        return (
          <div>
            {/* Pipeline Bar Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-semibold text-gray-900">Lead Pipeline</h3>
                {leadStatsLoading
                  ? <span className="text-xs text-gray-400 animate-pulse">Loading…</span>
                  : <span className="text-sm text-gray-500">{total.toLocaleString()} total</span>
                }
              </div>
              <p className="text-xs text-gray-400 mb-2">Leads grouped by pipeline stage</p>
              
              {/* Status Summary - below subtitle, above bars */}
              {!leadStatsLoading && pipelineData.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {pipelineData.map((item) => (
                    <div key={item.name} className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }}></span>
                      <span className="text-xs font-medium text-gray-600">{item.name}</span>
                      <span className="text-xs font-bold text-gray-800">{item.count}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {leadStatsLoading ? <Spinner /> : pipelineData.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No lead data available</div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(160, pipelineData.length * 44)}>
                  <BarChart data={pipelineData} layout="vertical" margin={{ left: 0, right: 28, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" width={92} tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomBarTooltip />} cursor={{ fill: '#f9fafb' }} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={26}>
                      {pipelineData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        );
      })()}

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Check if user has any quick action permissions */}
              {!hasPermission('CRM:LEADS:CREATE') && 
               !hasPermission('FINANCE:BILLS:CREATE') && 
               !hasPermission('INVENTORY:ITEMS:READ') && 
               !hasPermission('HRM:EMPLOYEES:READ') && 
               !hasPermission('CRM:STUDENTS:READ') && 
               !hasPermission('FINANCE:BUDGETS:READ') && 
               !hasPermission('REPORTS:SALES:READ') && 
               !hasPermission('REPORTS:INVENTORY:READ') && 
               !hasPermission('REPORTS:FINANCIAL:READ') && 
               !hasPermission('ADMIN:SETTINGS:READ') && (
                <div className="col-span-2 md:col-span-4 text-center py-8">
                  <div className="text-gray-400 mb-2">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-sm">No quick actions available</p>
                  <p className="text-gray-400 text-xs mt-1">Contact your administrator for access</p>
                </div>
              )}
              
              <PermissionGuard permission="CRM:LEADS:CREATE">
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">New Lead</span>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="FINANCE:BILLS:CREATE">
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">New Bill</span>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="INVENTORY:ITEMS:READ">
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Inventory</span>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="HRM:EMPLOYEES:READ">
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Employees</span>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="CRM:STUDENTS:READ">
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Students</span>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="FINANCE:BUDGETS:READ">
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-pink-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Budgets</span>
                </button>
              </PermissionGuard>

              <PermissionGuard permission={["REPORTS:SALES:READ", "REPORTS:INVENTORY:READ", "REPORTS:FINANCIAL:READ"]}>
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Reports</span>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="ADMIN:SETTINGS:READ">
                <button className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl hover:shadow-md transition-all group">
                  <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Settings</span>
                </button>
              </PermissionGuard>
            </div>
          </div>
        </div>

        {/* User Info Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Account Info
            </h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{String(user?.name || 'User')}</p>
                  <p className="text-sm text-gray-500">{String(user?.email || 'No email')}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-100 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Role</span>
                  <span className="text-sm font-medium text-gray-900">
                    {(() => {
                      // Handle primaryRole
                      if (user?.primaryRole && typeof user.primaryRole === 'string') {
                        return user.primaryRole;
                      }
                      
                      // Handle role property
                      if (user?.role) {
                        if (typeof user.role === 'string') return user.role;
                        if (typeof user.role === 'object' && user.role.roleCode) return user.role.roleCode;
                        if (typeof user.role === 'object' && user.role.code) return user.role.code;
                        if (typeof user.role === 'object' && user.role.name) return user.role.name;
                      }
                      
                      // Handle roles array
                      if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) {
                        const firstRole = user.roles[0];
                        if (typeof firstRole === 'string') return firstRole;
                        if (firstRole && typeof firstRole === 'object') {
                          if (firstRole.roleCode) return firstRole.roleCode;
                          if (firstRole.code) return firstRole.code;
                          if (firstRole.name) return firstRole.name;
                        }
                      }
                      
                      return 'USER';
                    })()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    (user?.status && String(user.status).toLowerCase() === 'active') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user?.status ? String(user.status) : 'active'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Email Verified</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    Boolean(user?.emailVerified) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {Boolean(user?.emailVerified) ? 'Verified' : 'Unverified'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Last Login</span>
                  <span className="text-sm text-gray-900">
                    {(() => {
                      if (!user?.lastLogin) return 'Never';
                      try {
                        const date = new Date(user.lastLogin);
                        return isNaN(date.getTime()) ? 'Never' : date.toLocaleDateString();
                      } catch (error) {
                        return 'Never';
                      }
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <CalendarView />
      )}
    </div>
  );
};

export default Dashboard;